import { useEffect, useState, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import styles from "./ArchieveReport.module.css";
import swal from "sweetalert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useSelector } from "react-redux";
import CustomTip from "../../Common/CustomToolTip/CustomTip";
import useApiServices from "../../Utils/useApiService";
import { FETCH_INVOICE } from "../../Utils/Constants";

async function generateInvoicePreview(data) {
    // Canvas-based preview generation using browser APIs only
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    // A4 dimensions in pixels at 72 DPI (points)
    const pageWidth = 595;
    const pageHeight = 842;
    const scale = 2; // For sharper image
    canvas.width = pageWidth * scale;
    canvas.height = pageHeight * scale;
    ctx.scale(scale, scale);

    // White background
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, pageWidth, pageHeight);

    // Border
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 1;
    ctx.strokeRect(20, 20, pageWidth - 40, pageHeight - 40);

    let y = 80;
    const margin = 40;
    const centerX = pageWidth / 2;
    const valueX = 280; // Adjusted for better alignment

    // Title
    ctx.font = "bold 18px Arial";
    ctx.fillStyle = "#000000";
    const title = "MERCHANT INVOICE";
    const titleMetrics = ctx.measureText(title);
    ctx.fillText(title, centerX - titleMetrics.width / 2, y);

    y += 60;

    // Details
    ctx.font = "normal 10px Arial";
    const details = [
        { label: "MID:", value: data.mid },
        { label: "Company Name:", value: data.companyName },
        { label: "Billing Cycle:", value: data.billingCycle },
        { label: "Start Date:", value: data.start },
        { label: "End Date:", value: data.end },
        { label: "Invoice Due Date:", value: data.invoiceDueDate },
        { label: "Total Transaction Amount:", value: data.totalTransactionAmount?.toLocaleString() || "0" },
    ];

    details.forEach((item) => {
        ctx.fillText(item.label, margin, y);
        ctx.fillText(String(item.value ?? ""), valueX, y);
        y += 18;
    });

    y += 20;

    // Charges
    const charges = [
        {
            label: "Service Charge:",
            value: `INR ${data.serviceCharge?.toFixed(2) || "0.00"}`,
        },
        {
            label: "GST (18%):",
            value: `INR ${data.gstOnServiceCharge?.toFixed(2) || "0.00"}`,
        },
    ];

    charges.forEach((item) => {
        ctx.fillText(item.label, margin, y);
        ctx.fillText(item.value, valueX, y);
        y += 18;
    });

    y += 10;

    // Separator line
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(margin, y);
    ctx.lineTo(margin + 300, y);
    ctx.stroke();

    y += 20;

    // Total
    ctx.font = "bold 12px Arial";
    ctx.fillText("TOTAL AMOUNT TO BE PAID", margin, y);
    ctx.fillText(`INR ${data.totalAmountToBePaid?.toFixed(2) || "0.00"}`, valueX, y);

    y += 10;

    // Bottom separator
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(margin, y);
    ctx.lineTo(margin + 300, y);
    ctx.stroke();

    // Convert canvas to blob
    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                resolve(null);
                return;
            }
            const url = URL.createObjectURL(blob);
            resolve({ url, blob });
        }, "image/png");
    });
}

const BillingDetails = () => {
    const { mid } = useSelector((state) => state.userData);
    const { apiCallFnforGet } = useApiServices();

    const [invoiceList, setInvoiceList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchInvoices = useCallback(async () => {
        if (!mid) {
            setInvoiceList([]);
            return;
        }

        try {
            setLoading(true);
            setError("");
            const res = await apiCallFnforGet(FETCH_INVOICE + mid);
            console.log(res, 'res!');
            if (res?.statusCode !== 200) {
                throw new Error(res?.message || "Failed to fetch invoice data");
            }

            setInvoiceList(res.data || []);
        } catch (err) {
            console.error(err);
            setError(err.message);
            swal("Error!", err.message, "error");
        } finally {
            setLoading(false);
        }
    }, [mid]);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    const handleDownloadInvoice = async (invoice) => {
        try {
            setLoading(true);

            const data = {
                mid: invoice.mid,
                companyName: invoice.companyName,
                billingCycle: invoice.billingCycle,
                start: invoice.start,
                end: invoice.end,
                invoiceDueDate: invoice.invoiceDueDate,
                totalTransactionAmount: invoice.totalTransactionAmount,
                gstOnServiceCharge: invoice.gstOnServiceCharge,
                serviceCharge: invoice.serviceCharge,
                totalAmountToBePaid: invoice.totalAmountToBePaid,
            };

            const result = await generateInvoicePreview(data);
            if (result && result.url) {
                const link = document.createElement("a");
                link.href = result.url;
                link.download = `invoice-${invoice.invoiceNumber || "document"}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // optional cleanup
                // URL.revokeObjectURL(result.url);
            }
        } catch (err) {
            console.error("Error downloading invoice:", err);
            swal("Error!", "Failed to download invoice", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleViewInvoice = (invoice) => {
        const preview = {
            mid: invoice.mid,
            companyName: invoice.companyName,
            billingCycle: invoice.billingCycle,
            start: invoice.start,
            end: invoice.end,
            invoiceDueDate: invoice.invoiceDueDate,
            totalTransactionAmount: invoice.totalTransactionAmount,
            serviceCharge: invoice.serviceCharge,
            gstOnServiceCharge: invoice.gstOnServiceCharge,
            totalAmountToBePaid: invoice.totalAmountToBePaid,
        };

        generateInvoicePreview(preview).then((res) => {
            if (res?.url) window.open(res.url, "_blank");
        });
    };

    const column = [
        { headerName: "Invoice No.", field: "invoiceNumber", minWidth: 180 },
        { headerName: "Company", field: "companyName", minWidth: 200 },
        { headerName: "Billing Cycle", field: "billingCycle", minWidth: 140 },
        { headerName: "Status", field: "status", minWidth: 120 },
        {
            headerName: "Total Txn Amt",
            field: "totalTransactionAmount",
            minWidth: 160,
            renderCell: (p) => `₹ ${p.row.totalTransactionAmount?.toLocaleString()}`
        },
        { headerName: "Due Date", field: "invoiceDueDate", minWidth: 160 },
        {
            headerName: "Actions",
            field: "actions",
            minWidth: 150,
            sortable: false,
            renderCell: (params) => {
                const invoice = params.row;
                return (
                    <div className="d-flex justify-content-center align-items-center gap-4">
                        <CustomTip size="18" placement="top" title="View Invoice">
                            <div style={{ cursor: "pointer" }} onClick={() => handleViewInvoice(invoice)}>
                                <VisibilityIcon fontSize="medium" />
                            </div>
                        </CustomTip>
                        <CustomTip size="18" placement="top" title="Download Invoice">
                            <div style={{ cursor: "pointer" }} onClick={() => handleDownloadInvoice(invoice)}>
                                <FileDownloadIcon fontSize="medium" />
                            </div>
                        </CustomTip>
                    </div>
                );
            }
        }
    ];

    return (
        <div className="p-3">
            <div className="card-header p-3 d-flex flex-column flex-md-row justify-content-between align-items-center">
                <h4 class="card-title">Billing Details</h4>
            </div>
            <div className={`mt-3 ${styles["DataGridHeight"]}`}>
                <DataGrid
                    rows={invoiceList}
                    columns={column}
                    autoHeight
                    loading={loading}
                    disableSelectionOnClick
                    hideFooter
                />
            </div>

            {error && <div className="text-danger text-center fs-5 mt-2">{error}</div>}
        </div>
    );
};

export default BillingDetails;